import DefaultLayout from '../../components/DefaultLayout';
import { ListingsFilters } from './ListingsFilters';
import { DefaultThirdHeader } from '../../components/DefaultLayout/DefaultThirdHeader';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useDebounce } from '../../../utils/useDebounce';
import { Button, Icon, Spinner } from '../../../ui';
import { Pagination } from 'antd';
import { MobileMenuDrawer } from 'ui/Drawer/Drawers/MobileMenuDrawer';
import { StyledMobileFilterFooter } from 'public/components/MobileLayout/MobileMenu/MobileFilterFooter/style';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledListings } from './style';
import { ListingsTopFilter } from './ListingsTopFilter';
import { options } from '../../../config/config';
import { paginationNavigationRenderer } from 'utils/paginationNavigationrenderer';
import PublicRoute from 'enums/publicRoutes';
import { ListingData, ListingFilters, ListingFilterOptions } from './types';
import { ListingCard } from './ListingCard';
import {
  useFilterDispatch,
  useFilterState,
} from 'public/context/FilterContext';
import { useCategoryDispatch } from '../../context';
import SearchAndFilter from 'public/components/DefaultLayout/SearchAndFilter';

export const ListingsPage = () => {
  const [filter, setFilter] = useState<ListingFilters>({});
  const debouncedSearch = useDebounce(filter.search, 500);
  const debouncedDistanceRadius = useDebounce(filter.distance_radius, 500);
  const effectiveFilter = useMemo(() => ({
    ...filter,
    search: debouncedSearch,
    distance_radius: debouncedDistanceRadius,
  }), [
    filter.status,
    filter.transaction_type,
    filter['main-category'],
    filter['sub-category'],
    filter['sub-subcategory'],
    filter.location,
    filter.lks_lat,
    filter.lks_long,
    filter.order_by,
    filter.order_direction,
    debouncedSearch,
    debouncedDistanceRadius,
  ]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [listings, setListings] = useState<ListingData[]>([]);
  const [filterOptions, setFilterOptions] = useState<ListingFilterOptions>({});
  const [filtersReady, setFiltersReady] = useState<boolean>(false);
  const dispatchFilter = useFilterDispatch();
  const { isOpenMobileFilter } = useFilterState();

  const dispatch = useCategoryDispatch();

  useEffect(() => {
    dispatch({
      type: 'SAVE_ACTIVE_CATEGORY',
      activeCategory: null,
    });
  }, []);

  const openCloseDrawer = useCallback(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isOpenMobileFilter: !isOpenMobileFilter },
    });
    addClassToHeader();
  }, [isOpenMobileFilter]);

  useEffect(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isFilter: true, openCloseDrawer },
    });
  }, [dispatchFilter, openCloseDrawer]);

  const addClassToHeader = () => {
    const header = document.getElementById('mobile-header-container');

    if (header) {
      if (!isOpenMobileFilter) {
        header.classList.add('filterOpen');
      } else {
        header.classList.remove('filterOpen');
      }
    }
  };

  const { isLoading, appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/wholesale/listings',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setTotal(response.total);
      setListings(response.data);
      setFilterOptions(response.filters || {});
    },
  });

  const handleListingRefetch = () => {
    appendData({
      filter: {
        ...effectiveFilter,
      },
      page: currentPage,
      page_size: pageSize,
    });
  };

  useEffect(() => {
    if (filtersReady) {
      handleListingRefetch();
    }
  }, [filtersReady, effectiveFilter, currentPage, pageSize]);

  useEffect(() => {
    setFilter({});
    setFiltersReady(true);
  }, []);

  const handleFilterChange = (newFilter: ListingFilters) => {
    const clearedFilter = Object.fromEntries(
      Object.entries(newFilter)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, value || null])
    );
    setFilter(clearedFilter);
    setCurrentPage(1);
  };

  const intl = useIntl();

  const pageSizeOptions = options.filters.pageSizeOptions;

  const breadcrumbs = [
    {
      route: '/',
      name: intl.formatMessage({ id: 'general.start' }),
    },
    {
      route: PublicRoute.WHOLESALE_LISTINGS,
      name: intl.formatMessage({ id: 'navigation.listings' }),
    },
  ];

  const drawer = () => {
    return (
      <MobileMenuDrawer
        title={intl.formatMessage({ id: 'product.filter' })}
        onClose={openCloseDrawer}
        open={isOpenMobileFilter}
        className="mobile-filter"
        closable={false}
        footer={
          <StyledMobileFilterFooter>
            <Button
              label={intl.formatMessage({ id: 'general.choose' })}
              icon={<Icon name={'mobile-filter-submit-arrow-icon'} />}
              onClick={() => {
                handleFilterChange(filter);
                openCloseDrawer();
              }}
            />
          </StyledMobileFilterFooter>
        }
      >
        <ListingsFilters
          setFilter={setFilter}
          filterOptions={filterOptions}
          filter={filter}
        />
      </MobileMenuDrawer>
    );
  };

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent className={'grey-background'}>
        <StyledListings>
          <DefaultThirdHeader
            label={intl.formatMessage({ id: 'listing.page_title' })}
            filters={
              <ListingsTopFilter
                total={total}
                per_page={pageSize}
                setFilter={(newFilter) =>
                  handleFilterChange({ ...filter, ...newFilter })
                }
                page_size_options={pageSizeOptions}
                setPageSize={setPageSize}
                filters={filter}
              />
            }
            breadcrumb={breadcrumbs}
          />
          <SearchAndFilter rootClassName="laptop-search-section-box" hideSearch />
          <div className="container listings-container">
            <div className={'listings-filters-container'}>
              <ListingsFilters
                setFilter={handleFilterChange}
                filterOptions={filterOptions}
                filter={filter}
              />
              <Spinner spinning={isLoading}>
                <div className={'listings-wrapper'}>
                  <div className={'listings-grid'}>
                    {listings.map((listing: ListingData, index) => (
                      <ListingCard key={`listing-${index}`} listing={listing} />
                    ))}
                  </div>

                  <div className={'pagination'}>
                    {total > pageSize && (
                      <Pagination
                        total={total}
                        showSizeChanger={false}
                        current={currentPage}
                        defaultPageSize={20}
                        pageSize={pageSize}
                        onChange={(page, pageSize) => {
                          setPageSize(pageSize);
                          setCurrentPage(page);
                        }}
                        itemRender={paginationNavigationRenderer}
                      />
                    )}
                  </div>
                </div>
              </Spinner>
            </div>
          </div>
          {drawer()}
        </StyledListings>
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};
